import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getProjectConfig } from '../../../getProjectConfig';

interface ItemSearchBarProps {
  disabled?: boolean;
  onItemSelected: (item: any) => void;
  initialValue?: string;
  className?: string;
  label?: string;
  helperText?: string;
}

const ItemSearchBar = ({ 
  disabled = false, 
  onItemSelected,
  initialValue = "",
  className,
  label,
  helperText
}: ItemSearchBarProps) => {
  const [t] = useTranslation('global');
  const apiURL: string | undefined = process.env.REACT_APP_API_URL;
  const token: string | undefined = localStorage.token;

  // Local state
  const [openDropdown, setOpenDropdown] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [inputValue, setInputValue] = useState(initialValue || "");
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Update input value when initial value changes
  useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  // For Search Autocomplete
  useEffect(() => {
    // Don't search if less than 3 characters or no change
    if (!inputValue || inputValue.length < 3) {
      setOptions([]);
      return;
    }
    
    const delayDebounceFn = setTimeout(() => {
      let active = true;
      const headers: object = { ContentType: 'application/json', Authorization: 'Bearer ' + token };

      setSearchLoading(true);
      
      (async () => {
        try {
          // Search items
          const qParamsItems = { 
            page: 0, 
            name: inputValue, 
            size: getProjectConfig().PAGES_SIZE, 
            paged: true, 
            isUniquePerCode: true 
          };
          
          const resItems = await axios.get(`${apiURL}/seller/getAllItems`, { 
            params: qParamsItems, 
            headers: headers 
          });
          
          const items = (resItems.data.data.itemPage !== undefined) 
            ? resItems.data.data.itemPage.content 
            : resItems.data.data.content;

          if (active) {
            setOptions(items);
          }
        } catch (error) {
          console.error("Error fetching search results:", error);
        } finally {
          if (active) {
            setSearchLoading(false);
          }
        }
      })();

      return () => {
        active = false;
      };
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [inputValue, apiURL, token]);

  return (
    <Autocomplete
      id="item-search-autocomplete" 
      style={{ width: '100%' }} 
      open={openDropdown && inputValue.length >= 3}
      onOpen={() => { 
        inputValue.length >= 3 ? setOpenDropdown(true) : setOpenDropdown(false); 
      }}
      onClose={() => { 
        setOpenDropdown(false); 
      }}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event: any, newValue: any | null) => {
        setSelectedItem(newValue);
        onItemSelected(newValue);
      }}
      getOptionSelected={(option, value) => option?.id === value?.id}
      getOptionLabel={(option) => option?.name ?? ''} 
      options={options}
      loading={searchLoading}
      loadingText={t('header.loading')}
      noOptionsText={
        inputValue.length < 3 
          ? t('header.min-three-chars') 
          : t('header.no-result')
      }
      clearOnBlur={false}
      className={className}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          id="search-keys"
          name="search-keys"  
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default ItemSearchBar;